import { useEffect, useState } from 'react';
import { BellRing, Check, Copy, Download, MonitorSmartphone, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { callBackend } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { isDesktopApp } from '@/lib/desktopApp';
import { currentPushSubscription, pushSupported, subscribePush, unsubscribePush } from '@/lib/push';
import { useCrm } from './CrmContext';
import { Toggle } from './fields';
import { buttonClass, errorMessage } from './format';

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const onMac = /Mac/i.test(navigator.platform || navigator.userAgent);

const LINKUS_VARIABLES = 'number={{.CallerNumber}}&name={{.CallerDisplayName}}&did={{.DIDNumber}}';

function useDesktopRelease() {
  const [release, setRelease] = useState<{ version: string; windows: string | null; mac?: string | null } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/desktop/latest', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then((value) => {
        if (!cancelled) setRelease(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return release;
}

function BrowserNotifications({ publicKey }: { publicKey: string }) {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void currentPushSubscription().then((subscription) => setOn(Boolean(subscription) && Notification.permission === 'granted'));
  }, []);

  const toggle = async (next: boolean) => {
    setBusy(true);
    try {
      if (next) {
        const subscription = await subscribePush(publicKey);
        await callBackend(backendApi.crmSettings.savePushSubscription, { ...subscription, userAgent: navigator.userAgent });
        toast.success('Pop-ups will show as notifications on this computer');
      } else {
        const endpoint = await unsubscribePush();
        if (endpoint) await callBackend(backendApi.crmSettings.removePushSubscription, { endpoint });
      }
      setOn(next);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Toggle
      checked={Boolean(on)}
      onChange={(next) => {
        if (!busy) void toggle(next);
      }}
      label="Notifications on this computer"
      description="Incoming calls show as a Windows or Mac notification even with no Yeastarify tab open, as long as the browser is running. Click it to open the caller."
    />
  );
}

export function PopupDelivery() {
  const { data, hasFeature } = useCrm();
  const release = useDesktopRelease();
  const [copied, setCopied] = useState(false);
  const [installable, setInstallable] = useState(() => Boolean((window as unknown as { __installPrompt?: Event }).__installPrompt));
  const installed = window.matchMedia?.('(display-mode: standalone)').matches;
  const inDesktopApp = isDesktopApp();
  const popups = hasFeature('screenPop');
  const linkusUrl = `${window.location.origin}/caller?${LINKUS_VARIABLES}`;

  useEffect(() => {
    const offer = (event: Event) => {
      (window as unknown as { __installPrompt?: Event }).__installPrompt = event;
      setInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', offer);
    return () => window.removeEventListener('beforeinstallprompt', offer);
  }, []);

  const install = async () => {
    const prompt = (window as unknown as { __installPrompt?: InstallPrompt }).__installPrompt;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice.catch(() => undefined);
    (window as unknown as { __installPrompt?: Event }).__installPrompt = undefined;
    setInstallable(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(linkusUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy; select the text and copy it');
    }
  };

  return (
    <Card animate={false}>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Pop-ups without Yeastarify open</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Ways to see who is calling while you work in other programs.</p>
      <div className="space-y-6 max-w-2xl">
        {popups && (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MonitorSmartphone className="w-4 h-4" /> Yeastarify desktop app (Windows and Mac)
            </h4>
            {inDesktopApp ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">You are using the desktop app. It sits in the {onMac ? 'menu bar' : 'tray'} and shows pop-ups in the corner of the screen.</p>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Sits in the system tray (the menu bar on a Mac), starts when you sign in, and shows the full caller card in the corner of the screen over any program.
                </p>
                {release?.windows || release?.mac ? (
                  <div className={`flex flex-wrap gap-2 ${onMac ? 'flex-row-reverse justify-end' : ''}`}>
                    {release.windows && (
                      <a href={release.windows} className={onMac ? buttonClass.secondary : buttonClass.primary} download>
                        <Download className="w-4 h-4" /> Download for Windows ({release.version})
                      </a>
                    )}
                    {release.mac && (
                      <a href={release.mac} className={onMac ? buttonClass.primary : buttonClass.secondary} download>
                        <Download className="w-4 h-4" /> Download for Mac ({release.version})
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Coming soon.</p>
                )}
                {release?.mac && onMac && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Open the download and drag Yeastarify into Applications. The first time you open it, macOS says it cannot check the app: click <b>Done</b>, then go to <b>System Settings → Privacy &amp; Security</b> and click <b>Open Anyway</b>.
                  </p>
                )}
              </>
            )}
          </section>
        )}

        {popups && !inDesktopApp && (
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BellRing className="w-4 h-4" /> In the browser
            </h4>
            {data?.pushPublicKey && pushSupported() ? (
              <BrowserNotifications publicKey={data.pushPublicKey} />
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">This browser cannot show notifications while Yeastarify is closed. Use Chrome or Edge, or the desktop app.</p>
            )}
            {!installed && (
              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <p>Install Yeastarify as an app for its own window and taskbar icon. In Edge or Chrome you can then set it to start when you sign in to Windows.</p>
                {installable ? (
                  <button type="button" onClick={() => void install()} className={buttonClass.secondary}>
                    <Download className="w-4 h-4" /> Install Yeastarify
                  </button>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Use the install icon at the right of the address bar, or the browser menu → Apps → Install Yeastarify.</p>
                )}
              </div>
            )}
          </section>
        )}

        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Phone className="w-4 h-4" /> Linkus
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Linkus desktop and web can open the caller in Yeastarify. In Linkus go to <b>Preferences → Features → Call Popup</b>, tick <b>Open Contact URL Using Custom Popup URL</b>, paste this address, and pick when to open it (we suggest <b>Answered</b>, so a tab does not open for every call that rings).
          </p>
          <div className="flex gap-2">
            <input readOnly value={linkusUrl} onFocus={(event) => event.target.select()} className="flex-1 min-w-0 px-3 py-2 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200" />
            <button type="button" onClick={() => void copy()} className={buttonClass.secondary}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The PBX administrator can set this for many extensions at once: Extension and Trunk → Extension → Edit → Linkus Clients. Needs PBX firmware 37.21.0.16 or later (Appliance), 83.21.0.16 (Software) or 84.21.0.16 (Cloud), and Linkus desktop 1.10.2 or later.
          </p>
        </section>
      </div>
    </Card>
  );
}
